/**
 * CompsApiDetail 通用API接口详情面板组件
 * 用于显示接口的详细信息，包括参数和响应结构
 */
import React from "react";
import { Table, Typography, Tag, Space, Card } from "antd";
import { COMPONENT_OUTLINE_BORDER } from "../theme/componentBorders";

const { Title, Text } = Typography;

// 方法颜色映射
const METHOD_COLORS: Record<string, string> = {
  GET: "blue",
  POST: "green",
  PUT: "orange",
  DELETE: "red",
  PATCH: "purple",
};

// 参数类型定义
export interface ApiParameter {
  name: string;
  type: string;
  required: boolean;
  description: string;
  example?: string;
}

// 响应字段类型定义
export interface ResponseField {
  name: string;
  type: string;
  description: string;
  example?: string;
}

// 接口详情类型定义
export interface ApiDetail {
  id: string;
  method: string;
  name: string;
  description: string;
  path: string;
  requiredPermission?: string;
  pathParams?: ApiParameter[];
  queryParams?: ApiParameter[];
  bodyParams?: ApiParameter[];
  responseFields: ResponseField[];
  responseExample?: string; // JSON 格式的响应示例
}

// 组件属性类型定义
export interface CompsApiDetailProps {
  title?: string;
  api?: ApiDetail;
  height?: number | string;
}

const CompsApiDetail: React.FC<CompsApiDetailProps> = ({
  title = "接口详情",
  api,
  height = "100%",
}) => {
  // 参数表格列配置
  const paramColumns = [
    {
      title: "参数名",
      dataIndex: "name",
      key: "name",
      width: 150,
      render: (text: string, record: ApiParameter) => (
        <Space>
          <Text strong>{text}</Text>
          {record.required && <Tag color="red">必填</Tag>}
        </Space>
      ),
    },
    {
      title: "类型",
      dataIndex: "type",
      key: "type",
      width: 100,
      render: (text: string) => <Tag color="cyan">{text}</Tag>,
    },
    {
      title: "描述",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "示例值",
      dataIndex: "example",
      key: "example",
      width: 150,
      render: (text: string) => (text ? <Text code>{text}</Text> : "-"),
    },
  ];

  // 响应字段表格列配置
  const responseColumns = [
    {
      title: "字段名",
      dataIndex: "name",
      key: "name",
      width: 200,
      render: (text: string) => <Text code>{text}</Text>,
    },
    {
      title: "类型",
      dataIndex: "type",
      key: "type",
      width: 100,
      render: (text: string) => <Tag color="cyan">{text}</Tag>,
    },
    {
      title: "描述",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "示例值",
      dataIndex: "example",
      key: "example",
      width: 150,
      render: (text: string) => (text ? <Text code>{text}</Text> : "-"),
    },
  ];

  return (
    <Card
      title={title}
      style={{
        flex: 1,
        height,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
      bodyStyle={{ flex: 1, overflow: "auto", padding: 16 }}
    >
      {api ? (
        <div style={{ height: "100%", overflow: "auto" }}>
          {/* 基本信息 */}
          <Space
            direction="vertical"
            size={16}
            style={{ width: "100%", marginBottom: "24px" }}
          >
            <div>
              <Title level={4} style={{ marginTop: 0, marginBottom: "8px" }}>
                <Space>
                  <Tag color={METHOD_COLORS[api.method]}>{api.method}</Tag>
                  {api.name}
                </Space>
              </Title>
              <Text type="secondary">{api.description}</Text>
            </div>

            <div>
              <Text strong>接口路径：</Text>
              <Text code style={{ fontSize: "14px", marginLeft: "8px" }}>
                {api.path}
              </Text>
            </div>

            {api.requiredPermission && (
              <div>
                <Text strong>所需权限：</Text>
                <Tag color="gold" style={{ marginLeft: "8px" }}>
                  {api.requiredPermission}
                </Tag>
              </div>
            )}
          </Space>

          {/* Path 参数 */}
          {api.pathParams && api.pathParams.length > 0 && (
            <div style={{ marginBottom: "24px" }}>
              <Title level={5}>Path 参数</Title>
              <Table
                size="small"
                columns={paramColumns}
                dataSource={api.pathParams}
                pagination={false}
                rowKey="name"
                bordered
              />
            </div>
          )}

          {/* Query 参数 */}
          {api.queryParams && api.queryParams.length > 0 && (
            <div style={{ marginBottom: "24px" }}>
              <Title level={5}>Query 参数</Title>
              <Table
                size="small"
                columns={paramColumns}
                dataSource={api.queryParams}
                pagination={false}
                rowKey="name"
                bordered
              />
            </div>
          )}

          {/* Body 参数 */}
          {api.bodyParams && api.bodyParams.length > 0 && (
            <div style={{ marginBottom: "24px" }}>
              <Title level={5}>Body 参数</Title>
              <Table
                size="small"
                columns={paramColumns}
                dataSource={api.bodyParams}
                pagination={false}
                rowKey="name"
                bordered
              />
            </div>
          )}

          {/* 响应数据结构 */}
          <div style={{ marginBottom: "24px" }}>
            <Title level={5}>响应数据结构</Title>
            <Table
              size="small"
              columns={responseColumns}
              dataSource={api.responseFields}
              pagination={false}
              rowKey="name"
              bordered
            />
          </div>

          {/* 响应数据示例 */}
          {api.responseExample && (
            <div style={{ marginBottom: "24px" }}>
              <Title level={5}>响应数据示例</Title>
              <Card
                size="small"
                style={{
                  backgroundColor: "#f5f5f5",
                  border: COMPONENT_OUTLINE_BORDER,
                }}
                bodyStyle={{ padding: "12px" }}
              >
                <pre
                  style={{
                    margin: 0,
                    padding: 0,
                    fontSize: "13px",
                    lineHeight: "1.6",
                    fontFamily:
                      "Monaco, Menlo, 'Ubuntu Mono', Consolas, 'source-code-pro', monospace",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    overflow: "auto",
                    maxHeight: "400px",
                    color: "#333",
                  }}
                >
                  {api.responseExample}
                </pre>
              </Card>
            </div>
          )}
        </div>
      ) : (
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Text type="secondary">请选择左侧接口查看详情</Text>
        </div>
      )}
    </Card>
  );
};

export default CompsApiDetail;

